import { CheckOutlined, CopyOutlined } from '@ant-design/icons';
import type { TooltipProps } from 'antd';
import { Typography } from 'antd';
import type { CopyConfig } from 'antd/es/typography/Base';
import type { CSSProperties, ReactNode } from 'react';
import React, { useMemo } from 'react';

export const TYPOGRAPHY_COPY_TOOLTIPS: [ReactNode, ReactNode] = [
  '复制',
  '已复制到剪贴板',
];

export const DEFAULT_COPY_ICON_COLOR = '#121928';
const DEFAULT_COPY_ICON_SIZE = 14;

export function buildCopyIcons(
  size: number = DEFAULT_COPY_ICON_SIZE,
  color: string = DEFAULT_COPY_ICON_COLOR,
): CopyConfig['icon'] {
  const iconStyle = { fontSize: size, color };
  return [
    <CopyOutlined key="copy" style={iconStyle} />,
    <CheckOutlined key="copied" style={iconStyle} />,
  ];
}

export function buildTypographyCopyable(
  text: string,
  config?: Omit<CopyConfig, 'text'>,
): CopyConfig {
  return {
    tooltips: TYPOGRAPHY_COPY_TOOLTIPS,
    ...config,
    text,
  };
}

export type CopyIconPosition = 'start' | 'end';

export interface EllipsisTextProps {
  children?: ReactNode;
  /** 展示行数，默认 1；大于 1 时多行省略 */
  lines?: number;
  className?: string;
  style?: CSSProperties;
  /**
   * Tooltip 内容；默认取 children 文本。
   * 传 `false` 禁用 tooltip；也可传入 antd TooltipProps（如 styles 调整宽度）。
   */
  tooltip?: ReactNode | boolean | TooltipProps;
  /** 块级展示并允许在 flex 布局中收缩省略 */
  block?: boolean;
  /** 使用 Typography 内置复制；`true` 时复制 children 文本 */
  copyable?: boolean | CopyConfig;
  /** 复制图标字号，不与正文共用 font-size 时传入 */
  copyIconSize?: number;
  /** 复制按钮位置：`end` 在文字后面（默认），`start` 在文字前面 */
  copyIconPosition?: CopyIconPosition;
}

function toPlainText(node: ReactNode): string | undefined {
  if (node === null || node === undefined || typeof node === 'boolean') {
    return undefined;
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  return undefined;
}

const EllipsisText: React.FC<EllipsisTextProps> = ({
  children,
  lines = 1,
  className,
  style,
  tooltip = true,
  block = false,
  copyable = false,
  copyIconSize,
  copyIconPosition = 'end',
}) => {
  const plainText = toPlainText(children);

  const tooltipContent = useMemo(() => {
    if (tooltip === false) {
      return false;
    }
    if (tooltip !== true) {
      return tooltip;
    }
    return plainText;
  }, [plainText, tooltip]);

  const resolvedCopyable = useMemo(() => {
    if (!copyable) {
      return false;
    }
    if (copyable === true) {
      return plainText
        ? buildTypographyCopyable(plainText, {
            icon: buildCopyIcons(copyIconSize),
          })
        : false;
    }
    const text =
      typeof copyable.text === 'string'
        ? copyable.text
        : copyable.text
          ? undefined
          : plainText;
    if (!text && !copyable.text) {
      return false;
    }
    return {
      tooltips: TYPOGRAPHY_COPY_TOOLTIPS,
      text,
      ...copyable,
      ...(!copyable.icon ? { icon: buildCopyIcons(copyIconSize) } : {}),
    };
  }, [copyable, copyIconSize, plainText]);

  const isMultiLine = lines > 1;

  const mergedStyle = useMemo<CSSProperties>(
    () => ({
      marginBottom: 0,
      ...(block
        ? {
            // 多行省略依赖 antd 的 -webkit-box，不能覆盖为 block
            ...(!isMultiLine ? { display: 'block' } : {}),
            width: '100%',
            minWidth: 0,
          }
        : {}),
      ...style,
    }),
    [block, isMultiLine, style],
  );
  const TypographyComponent = isMultiLine
    ? Typography.Paragraph
    : Typography.Text;

  const ellipsis = isMultiLine
    ? { rows: lines, tooltip: tooltipContent }
    : { tooltip: tooltipContent };

  return (
    <TypographyComponent
      className={className}
      style={mergedStyle}
      ellipsis={ellipsis}
      copyable={resolvedCopyable}
      actions={
        !isMultiLine && resolvedCopyable
          ? { placement: copyIconPosition }
          : undefined
      }
    >
      {children}
    </TypographyComponent>
  );
};

export default EllipsisText;
